import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";

/**
 * Fail-closed activation check.
 * Returns true ONLY if user has a profile with membership_selected_at set.
 * Uses the server-side is_activated_member function (SECURITY DEFINER).
 * 
 * SYSTEM RULE: Auth user exists ≠ system member.
 * Dashboard access requires: isActivated === true
 * 
 * CRITICAL: isLoading must be true whenever we haven't definitively checked
 * activation status. This prevents ProtectedRoute from misclassifying
 * authenticated users as non-activated during auth hydration.
 */
export function useIsActivatedMember() {
  const { user, loading: authLoading } = useAuth();

  const { data: isActivated, isLoading: queryLoading } = useQuery({
    queryKey: ["is-activated-member", user?.id],
    queryFn: async (): Promise<boolean> => {
      if (!user?.id) return false;

      const { data, error } = await supabase.rpc("is_activated_member", {
        _user_id: user.id,
      });

      if (error) {
        console.error("[useIsActivatedMember] RPC error:", error);
        // Fail-closed: if we can't check, deny access
        return false;
      }

      return data === true;
    },
    enabled: !!user?.id,
    staleTime: 30_000, // Cache for 30s to avoid excessive calls
    retry: 2,
  });

  // CRITICAL FIX: Consider loading if auth is still resolving OR if the
  // activation query hasn't completed yet. Previously, when user was null
  // during hydration, isLoading was false and isActivated was false,
  // causing ProtectedRoute to redirect to onboarding for valid admins
  // opening preview in a new tab.
  const isLoading = authLoading || (!!user?.id && queryLoading);

  return {
    isActivated: isActivated ?? false,
    isLoading,
  };
}
